/**
 * App.js — entry point (wiring only)
 *
 * This file was refactored from a single 9,953-line monolith into focused modules.
 * See REFACTORING.md for the full module map and migration notes.
 *
 * Module structure:
 *
 *  src/
 *  ├── api/
 *  │   └── client.js           — getIdToken, apiPost, callAI, callAISecure, getAIUsage
 *  ├── constants/
 *  │   └── index.js            — DEFAULT_FMT, DEFAULT_CV, DEMO_CV, TEMPLATES, THEMES,
 *  │                             IIM_COLOR_THEMES, FONTS, BULLET_STYLES, HCOLORS,
 *  │                             ACOLORS, LEVELS, AI_PROVIDERS, DEFAULT_AI_CFG,
 *  │                             GALLERY_CATEGORIES, DEFAULT_PINNED, WEAK_PHRASES_LIST
 *  ├── utils/
 *  │   ├── text.js             — uid, escXml, parseText, mdToWordRuns
 *  │   └── docxBuilder.js      — buildDocxBlob
 *  ├── components/
 *  │   ├── ui/
 *  │   │   ├── BulletList.jsx       — <BulletList>
 *  │   │   ├── RichTextarea.jsx     — <RichTextarea>
 *  │   │   ├── GradeInput.jsx       — <GradeInput>
 *  │   │   ├── PeriodPicker.jsx     — <PeriodPicker>
 *  │   │   ├── AppModalView.jsx     — <AppModalView>
 *  │   │   ├── EmptyState.jsx       — <EmptyState>
 *  │   │   ├── GripIcon.jsx         — <GripIcon>, <GripHandle>
 *  │   │   └── Item.jsx             — <Item>, <BulletRow>
 *  │   ├── templates/
 *  │   │   ├── shared.jsx           — RenderProjects, RenderLanguages, RenderCustomSections,
 *  │   │   │                          calcDuration, BulletList (template variant)
 *  │   │   ├── TplIIMOriginal.jsx
 *  │   │   ├── TplClassic.jsx
 *  │   │   ├── TplModern.jsx
 *  │   │   ├── TplMinimal.jsx
 *  │   │   ├── TplExecutive.jsx
 *  │   │   ├── TplATS.jsx
 *  │   │   ├── TplChronological.jsx
 *  │   │   ├── TplFunctional.jsx
 *  │   │   ├── TplHybrid.jsx
 *  │   │   ├── TplCreative.jsx
 *  │   │   ├── TplInfographic.jsx
 *  │   │   ├── TplSidebar.jsx
 *  │   │   ├── TplTech.jsx
 *  │   │   ├── TplFinance.jsx
 *  │   │   ├── TplAcademic.jsx
 *  │   │   ├── TplMedical.jsx
 *  │   │   ├── TplMarketing.jsx
 *  │   │   ├── TplTimeline.jsx
 *  │   │   ├── TplEuropean.jsx
 *  │   │   ├── TplCompact.jsx
 *  │   │   ├── TplBold.jsx
 *  │   │   ├── TplElegant.jsx
 *  │   │   ├── TplStartup.jsx
 *  │   │   ├── TplLaw.jsx
 *  │   │   └── index.js             — TPL_MAP (all templates wrapped in React.memo)
 *  │   ├── editor/
 *  │   │   ├── CVEditor.jsx         — the main editor shell (was lines 3181–8861)
 *  │   │   ├── TemplateGallery.jsx  — template picker modal
 *  │   │   ├── OnboardingWizard.jsx — new-user setup flow
 *  │   │   └── SharedCVViewer.jsx   — read-only share-link view
 *  │   ├── auth/
 *  │   │   └── LoginScreen.jsx      — login / register / reset screen
 *  │   └── admin/
 *  │       └── AdminPanel.jsx       — admin user management panel
 *  ├── App.js                  ← YOU ARE HERE (wiring only, ~60 lines)
 *  ├── firebase.js
 *  ├── storage.js
 *  └── index.css
 */

import React from "react";
import { auth, db } from "./firebase";
import "./storage"; // Firestore-backed window.storage — must be after Firebase init
import {
  signOut, onAuthStateChanged,
  signInWithCustomToken,
} from "firebase/auth";
import {
  doc, setDoc, getDoc, getDocs, deleteDoc, updateDoc,
  collection, onSnapshot, serverTimestamp, runTransaction,
} from "firebase/firestore";

import { apiPost }        from "./api/client";
import LoginScreen        from "./components/auth/LoginScreen";
import { AppModalView }   from "./components/ui/AppModalView";
import { AlertTriangle, LoaderIcon } from "./components/ui/Icons";

// Lazy-loaded: not needed for the anonymous landing/login experience, so
// keeping them out of the initial bundle improves first-load performance.
const CVEditor = React.lazy(() =>
  import("./components/editor/CVEditor").then(m => ({ default: m.CVEditor })));
const SharedCVViewer = React.lazy(() =>
  import("./components/editor/SharedCVViewer").then(m => ({ default: m.SharedCVViewer })));
const PortfolioViewer = React.lazy(() =>
  import("./components/PortfolioViewer").then(m => ({ default: m.PortfolioViewer })));
const PublicChecker = React.lazy(() =>
  import("./components/PublicChecker").then(m => ({ default: m.PublicChecker })));
const JdAnalyzer = React.lazy(() =>
  import("./components/JdAnalyzer").then(m => ({ default: m.JdAnalyzer })));
const AdminPanel = React.lazy(() => import("./components/admin/AdminPanel"));

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div style={{minHeight:"100vh",background:"#0a0f1e",display:"flex",alignItems:"center",
          justifyContent:"center",fontFamily:"sans-serif",color:"#f87171",padding:32}}>
          <div style={{maxWidth:480,textAlign:"center"}}>
            <div style={{marginBottom:12,display:"flex",justifyContent:"center"}}>
              <AlertTriangle size={40} color="#f87171" strokeWidth={1.5} />
            </div>
            <h2 style={{marginBottom:8}}>Something went wrong</h2>
            <p style={{color:"#94a3b8",marginBottom:20,fontSize:14}}>
              {this.state.error.message}
            </p>
            <button onClick={()=>window.location.reload()}
              style={{padding:"10px 24px",background:"#1d4ed8",color:"#fff",border:"none",
                borderRadius:8,cursor:"pointer",fontSize:14}}>
              Reload page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function AppInner() {
  const [currentUser,   setCurrentUser]   = React.useState(undefined); // undefined = loading
  const [userProfile,   setUserProfile]   = React.useState(null);
  const [users,         setUsers]         = React.useState([]);
  const [showAdmin,     setShowAdmin]     = React.useState(false);
  const [impersonating, setImpersonating] = React.useState(null);
  const [appNotice,     setAppNotice]     = React.useState(null);
  const [sharedCV,      setSharedCV]      = React.useState(null);
  const [sharedCVLoaded,setSharedCVLoaded]= React.useState(false);
  const [portfolio,     setPortfolio]     = React.useState(null);
  const [portfolioLoaded,setPortfolioLoaded]=React.useState(false);
  const [ssoLoading,    setSsoLoading]    = React.useState(false);
  const [ssoError,      setSsoError]      = React.useState(null);
  const [appSettings,   setAppSettings]   = React.useState({});

  // ── SSO token handler — fires before auth listener, clears ?sso= from URL ──
  // When OutreachOS redirects here with ?sso=<customToken>, we call
  // signInWithCustomToken so the user lands directly in the editor.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ssoToken = params.get("sso");
    if (!ssoToken) return;

    // Store referrer so the topbar can show a "Back to OutreachOS" button
    sessionStorage.setItem("cursivo_referrer", "outreachos");

    // Scrub the token from the URL immediately (security hygiene)
    params.delete("sso");
    const cleanSearch = params.toString();
    const cleanUrl = window.location.pathname + (cleanSearch ? "?" + cleanSearch : "");
    window.history.replaceState({}, "", cleanUrl);

    setSsoLoading(true);
    signInWithCustomToken(auth, ssoToken)
      .catch((e) => {
        console.error("SSO sign-in failed:", e.message);
        setSsoError("Auto sign-in failed. Please log in manually.");
      })
      .finally(() => setSsoLoading(false));
  }, []); // eslint-disable-line


  // ── Shared-CV referral handler ────────────────────────────────────────────
  // When someone clicks "Build yours →" / "Use this template →" from a shared
  // resume page (SharedCVViewer), they land here with ?ref=shared&template=<id>.
  // Stash both in sessionStorage (survives the redirect into onboarding) so
  // OnboardingWizard can pre-select that template instead of the generic
  // industry default, and so attribution isn't lost. Doesn't touch auth flow.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    const tpl = params.get("template");
    if (ref === "shared") sessionStorage.setItem("cursivo_referrer", "shared_cv");
    if (tpl) sessionStorage.setItem("cursivo_incoming_template", tpl);
    if (ref || tpl) {
      params.delete("ref");
      params.delete("template");
      const cleanSearch = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (cleanSearch ? "?" + cleanSearch : ""));
    }
  }, []); // eslint-disable-line

  // ── LinkedIn OAuth token handler — mirrors the SSO handler above ─────────
  // When /api/auth/linkedin/callback redirects here with ?liToken=<customToken>,
  // we sign the user in with signInWithCustomToken — same mechanism as SSO.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const liToken = params.get("liToken");
    const liError = params.get("error");
    const isNewLinkedInUser = params.has("isNewLinkedInUser");

    // Surface OAuth errors (user denied, CSRF, etc.)
    if (liError === "signup_closed") {
      setSsoError("New signups are currently closed. Please check back later.");
      params.delete("error");
      const cleanUrl = window.location.pathname + (params.toString() ? "?" + params.toString() : "");
      window.history.replaceState({}, "", cleanUrl);
      return;
    }
    if (liError && liError.startsWith("linkedin_")) {
      const messages = {
        linkedin_denied:   "LinkedIn sign-in was cancelled.",
        linkedin_csrf:     "LinkedIn sign-in failed (security check). Please try again.",
        linkedin_no_email: "LinkedIn did not share your email. Please allow email access and try again.",
        linkedin_failed:   "LinkedIn sign-in failed. Please try again.",
      };
      setSsoError(messages[liError] || "LinkedIn sign-in failed.");
      params.delete("error");
      const cleanUrl = window.location.pathname + (params.toString() ? "?" + params.toString() : "");
      window.history.replaceState({}, "", cleanUrl);
      return;
    }

    if (!liToken) return;

    // Mark that we're processing LinkedIn OAuth so we don't redirect to /landing
    sessionStorage.setItem("cursivo_referrer", "linkedin");

    // If this is a new LinkedIn user, flag it for the signup prompt
    if (isNewLinkedInUser) {
      sessionStorage.setItem("showLinkedInSignupPrompt", "true");
    }

    // Scrub token from URL immediately (security hygiene)
    params.delete("liToken");
    params.delete("isNewLinkedInUser");
    const cleanUrl = window.location.pathname + (params.toString() ? "?" + params.toString() : "");
    window.history.replaceState({}, "", cleanUrl);

    setSsoLoading(true);
    signInWithCustomToken(auth, liToken)
      .catch((e) => {
        console.error("LinkedIn sign-in failed:", e.message);
        setSsoError("LinkedIn sign-in failed. Please try again or use another method.");
      })
      .finally(() => setSsoLoading(false));
  }, []); // eslint-disable-line

  // ── Firebase Auth listener ─────────────────────────────────────────────────
  React.useEffect(() => {
    let profileUnsub = null;

    const authUnsub = onAuthStateChanged(auth, (firebaseUser) => {
      if (profileUnsub) {
        profileUnsub();
        profileUnsub = null;
      }

      if (firebaseUser) {
        localStorage.setItem("cursivo_logged_in", "true");
        setCurrentUser(firebaseUser);

        // Stamp last login time — only if the doc already exists to avoid creating stub docs
        // with no name/email that pollute the admin user list.
        getDoc(doc(db, "users", firebaseUser.uid)).then((snap) => {
          if (snap.exists()) {
            // Patch missing name/email from Firebase Auth in case the profile write failed at registration
            const data = snap.data();
            const patch = { lastLoginAt: serverTimestamp() };
            if (!data.email && firebaseUser.email) patch.email = firebaseUser.email.toLowerCase();
            if (!data.name && (firebaseUser.displayName || firebaseUser.email))
              patch.name = firebaseUser.displayName || firebaseUser.email.split("@")[0];
            setDoc(doc(db, "users", firebaseUser.uid), patch, { merge: true }).catch(() => {});
          }
        }).catch(() => {});

        // Listen to the user profile in real-time
        profileUnsub = onSnapshot(doc(db, "users", firebaseUser.uid), (snap) => {
          if (snap.exists()) {
            const profile = {
              id: firebaseUser.uid,
              ...snap.data(),
              createdAt: snap.data().createdAt?.toDate?.()?.toISOString?.()?.slice(0,10) || ""
            };
            setUserProfile(profile);
          } else {
            setUserProfile(null);
            
            // Sign out ONLY if this isn't a brand-new registration session that is still writing its Firestore document.
            // firebaseUser.metadata.creationTime gives the exact timestamp of account creation in Firebase Auth.
            // Registration awaits this setDoc before returning, but the auth-state
            // change (which triggers this listener) fires the instant createUser
            // resolves — before that write lands, not after — so a slow/flaky
            // connection can outlast a short window. 30s trades a little extra
            // tolerance for not force-signing-out someone mid-registration.
            if (!isBrandNew) {
              // Auto-heal missing profile document for existing Firebase Auth user account
              const autoProfile = {
                name: firebaseUser.displayName || (firebaseUser.email ? firebaseUser.email.split("@")[0] : "User"),
                email: (firebaseUser.email || "").toLowerCase(),
                role: "user",
                status: "active",
                createdAt: serverTimestamp(),
                lastLoginAt: serverTimestamp(),
              };
              setDoc(doc(db, "users", firebaseUser.uid), autoProfile, { merge: true })
                .catch((e) => {
                  console.error("Failed to auto-heal profile:", e);
                  signOut(auth);
                });
            }
          }
        }, (error) => {
          console.error("Error listening to user profile:", error);
          setUserProfile(null);
        });
      } else {
        localStorage.removeItem("cursivo_logged_in");
        setCurrentUser(null);
        setUserProfile(null);
      }
    });

    return () => {
      authUnsub();
      if (profileUnsub) profileUnsub();
    };
  }, []);

  // ── Admin: load all users ─────────────────────────────────────────────────
  React.useEffect(() => {
    if (!userProfile || userProfile.role !== "admin") return;
    const unsub = onSnapshot(collection(db, "users"), (snap) => {
      setUsers(snap.docs.map(d => ({
        id: d.id, ...d.data(),
        createdAt: d.data().createdAt?.toDate?.()?.toISOString?.()?.slice(0,10) || "",
      })));
    });
    return unsub;
  }, [userProfile]);

  // ── Shared CV detection ───────────────────────────────────────────────────
  React.useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("share");
    if (!token) { setSharedCVLoaded(true); return; }
    if (!window.storage?.get) {
      setSharedCVLoaded(true);
      return;
    }

    window.storage.get(`cv-share-${token}`, true)
      .then(r => {
        if (r?.value) {
          setSharedCV({ ...JSON.parse(r.value), token });
          // Read-then-write through window.storage.get/set (two separate,
          // non-atomic round-trips) drops a view record whenever two people
          // open the same link close together — both read the same array
          // before either write lands, so the second set() overwrites the
          // first's append. Going straight through a Firestore transaction
          // (same "cv_data" collection storage.js itself targets for shared
          // keys) makes the read+append+write atomic without changing the
          // stored shape ShareAnalyticsPanel.jsx already reads.
          const viewKey = `cv-share-views-${token}`;
          runTransaction(db, async (tx) => {
            const ref = doc(db, "cv_data", viewKey);
            const snap = await tx.get(ref);
            const views = snap.exists() && snap.data().value ? JSON.parse(snap.data().value) : [];
            views.push({
              ts: Date.now(),
              lang: navigator.language || "",
              mobile: /Mobi|Android/i.test(navigator.userAgent),
            });
            tx.set(ref, { value: JSON.stringify(views.slice(-50)), updatedAt: serverTimestamp() });
          }).catch(() => {});
        }
      })
      .catch(() => {})
      .finally(() => setSharedCVLoaded(true));
  }, []);

  // ── Portfolio detection (/p/{username} via hosting rewrite, or ?p= fallback) ─
  React.useEffect(() => {
    const pathMatch = window.location.pathname.match(/^\/p\/([a-z0-9-]{3,30})\/?$/i);
    const username = (pathMatch ? pathMatch[1] : new URLSearchParams(window.location.search).get("p") || "").toLowerCase();
    if (!username || !window.storage?.get) { setPortfolioLoaded(true); return; }
    window.storage.get(`cv-portfolio-${username}`, true)
      .then(r => {
        if (r?.value) {
          const doc = JSON.parse(r.value);
          setPortfolio({ payload: doc.payload, theme: doc.theme, username });
        }
      })
      .catch(() => {})
      .finally(() => setPortfolioLoaded(true));
  }, []);

  // ── App settings listener (Firestore settings/app) ──────────────────────
  // Loaded for ALL users (maintenance mode, login flags affect everyone)
  React.useEffect(() => {
    const unsub = onSnapshot(doc(db, "settings", "app"), (snap) => {
      if (snap.exists()) setAppSettings(snap.data());
    }, () => {}); // silently ignore permission errors (user not signed in yet)
    return unsub;
  }, []);

  // ── User management handlers ──────────────────────────────────────────────
  const handleAddUser = async ({ name, email, password, role }) => {
    // IMPORTANT: createUserWithEmailAndPassword is intentionally NOT used here.
    // Calling it client-side auto-signs the admin out and logs them in as the
    // new user. Instead we call the Admin SDK via a server-side Cloud Function
    // which creates the account without affecting the caller's auth session.
    const trimEmail = email.trim().toLowerCase();
    const res = await apiPost("admin/createUser", { name: name.trim(), email: trimEmail, password, role });
    if (!res.success) {
      // adminCreateUser (functions/index.js) calls admin.auth().createUser()
      // — the Admin SDK, which uses different error codes than the client
      // SDK ("auth/email-already-exists", not "-in-use"). Both are mapped
      // here since it costs nothing and guards against either surfacing.
      const friendly = {
        "auth/email-already-exists": "Email already in use.",
        "auth/email-already-in-use": "Email already in use.",
        "auth/invalid-email":        "Invalid email address.",
        "auth/invalid-password":     "Password is too weak (min 6 chars).",
        "auth/weak-password":        "Password is too weak (min 6 chars).",
      };
      const code = res.code || "";
      throw Object.assign(new Error(friendly[code] || res.error || "Failed to create user."), { code });
    }
  };

  const handleChangeRole = async (userId, newRole) => {
    await setDoc(doc(db, "users", userId), { role: newRole }, { merge: true });
  };

  const handleDeleteUser = async (userId) => {
    // Remove the Firebase Auth account first (via Admin SDK) so the email
    // can be re-used for a fresh signup later. Firestore doc removed after.
    // We fire-and-forget the Auth deletion since a missing Auth account is
    // non-fatal for Firestore cleanup; adminDeleteUser already tolerates
    // auth/user-not-found internally.
    try {
      await apiPost("admin/deleteUser", { uid: userId });
    } catch (_) {
      // Non-fatal: Firestore cleanup continues even if Auth deletion fails
    }
    await deleteDoc(doc(db, "users", userId));
    if (impersonating === userId) setImpersonating(null);
  };

  const handleUpdateUser = async (userId, { name, email, role, plan }) => {
    // Admin-only path (see firestore.rules' isAdmin() write grant on
    // /users/{userId}) — this is the real lever for a user's AI provider/
    // model/monthly-credit allocation (functions/planConfig.js's PLANS),
    // not the client-side "API key" settings that used to exist in
    // AISettingsPanel and were never actually wired to any real AI call.
    await setDoc(doc(db, "users", userId), { name, email, role, ...(plan ? { plan } : {}) }, { merge: true });
  };

  const handleResetPassword = async (userId, newPassword) => {
    // Firebase Admin SDK is required to set passwords server-side.
    // We store a flag so the user is prompted to change on next login.
    await setDoc(doc(db, "users", userId), { forcePwReset: true, updatedAt: serverTimestamp() }, { merge: true });
    // For actual password reset, trigger email via Firebase Auth
    throw new Error("Use Firebase Console or send a password reset email via Auth.");
  };

  const handleSuspendUser = async (userId, suspended) => {
    await setDoc(doc(db, "users", userId), { status: suspended ? "suspended" : "active" }, { merge: true });
  };

  const handleSaveSettings = async (settingsObj) => {
    await setDoc(doc(db, "settings", "app"), { ...settingsObj, updatedAt: serverTimestamp() });
  };

  const handleLogout = async () => {
    await signOut(auth);
    setImpersonating(null);
    setShowAdmin(false);
  };

  // ── Render ────────────────────────────────────────────────────────────────
  const Spinner = () => (
    <div style={{minHeight:"100vh",background:"#0a0f1e",display:"flex",alignItems:"center",
      justifyContent:"center",fontFamily:"sans-serif"}}>
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
      <span style={{animation:"spin 1s linear infinite",color:"#64748b",display:"inline-flex"}}>
        <LoaderIcon size={20} color="#64748b" strokeWidth={2} />
      </span>
    </div>
  );

  if (!sharedCVLoaded || !portfolioLoaded || currentUser === undefined || ssoLoading) return <Spinner />;
  if (/^\/check\/?$/.test(window.location.pathname) || new URLSearchParams(window.location.search).has("check"))
    return <React.Suspense fallback={<Spinner />}><PublicChecker /></React.Suspense>;
  {
    const jdMatch = window.location.pathname.match(/^\/jd(?:\/([a-z0-9-]{3,80}))?\/?$/i);
    if (jdMatch) return <React.Suspense fallback={<Spinner />}><JdAnalyzer slug={jdMatch[1] ? jdMatch[1].toLowerCase() : null} /></React.Suspense>;
  }
  if (portfolio) return <React.Suspense fallback={<Spinner />}><PortfolioViewer data={portfolio} /></React.Suspense>;
  if (sharedCV)  return <React.Suspense fallback={<Spinner />}><SharedCVViewer data={sharedCV} /></React.Suspense>;
  if (!currentUser || !userProfile) {
    // ── Route unauthenticated visitors based on auth intent in URL ──
    // No intent → send to landing (so first-time visitors see the value prop,
    // testimonials, pricing — not a naked login form).
    // ?login=1 or ?signup=1 → show the auth screen (initial tab follows param).
    // Coming via OutreachOS SSO → show login directly, not landing page.
    const url = typeof window !== "undefined" ? new URL(window.location.href) : null;
    let wantsSignup  = url?.searchParams.has("signup");
    let wantsLogin   = url?.searchParams.has("login");

    // Fallback: check sessionStorage if query params missing (in case they got lost)
    const authIntent = sessionStorage.getItem("auth_intent");
    if (authIntent === "signup" && !wantsSignup && !wantsLogin) wantsSignup = true;
    if (authIntent === "login" && !wantsSignup && !wantsLogin) wantsLogin = true;

    // Clear the intent after reading it (one-time use)
    if (authIntent) sessionStorage.removeItem("auth_intent");

    const referrer     = sessionStorage.getItem("cursivo_referrer");
    const fromSSO      = referrer === "outreachos" || referrer === "linkedin";
    return <LoginScreen initialMode={wantsSignup ? "register" : "login"} ssoError={ssoError} appSettings={appSettings} />;
  }

  const activeUserId  = impersonating || userProfile.id;
  const activeProfile = impersonating ? (users.find(u => u.id === impersonating) || userProfile) : userProfile;

  // Maintenance mode — shown to non-admin users only
  if (appSettings.maintenanceMode && userProfile.role !== "admin") {
    return (
      <div style={{ minHeight:"100vh", background:"#0a0f1e", display:"flex",
        alignItems:"center", justifyContent:"center", fontFamily:"sans-serif",
        padding:"24px", textAlign:"center" }}>
        <div style={{ maxWidth:440 }}>
          <div style={{ fontSize:48, marginBottom:16 }}>🔧</div>
          <div style={{ color:"#f1f5f9", fontSize:22, fontWeight:700, marginBottom:10 }}>
            Down for maintenance
          </div>
          <div style={{ color:"#94a3b8", fontSize:15, lineHeight:1.6, marginBottom:28 }}>
            {appSettings.maintenanceMessage || "We're doing some maintenance. Back shortly!"}
          </div>
          <button onClick={handleLogout} style={{
            background:"none", border:"1px solid #334155", color:"#64748b",
            borderRadius:8, padding:"8px 20px", cursor:"pointer", fontSize:13, fontFamily:"inherit"
          }}>Sign out</button>
        </div>
      </div>
    );
  }

  return (
    <>
      <React.Suspense fallback={<Spinner />}>
      {showAdmin && (
        <AdminPanel
          users={users}
          currentUser={userProfile}
          onClose={() => setShowAdmin(false)}
          onAddUser={handleAddUser}
          onDeleteUser={handleDeleteUser}
          onChangeRole={handleChangeRole}
          onImpersonate={uid => { setImpersonating(uid); setShowAdmin(false); }}
          impersonating={impersonating}
          onStopImpersonating={() => setImpersonating(null)}
          onUpdateUser={handleUpdateUser}
          onResetPassword={handleResetPassword}
          onSuspendUser={handleSuspendUser}
          appSettings={appSettings}
          onSaveSettings={handleSaveSettings}
          onClearActivity={async () => {
            const { getDocs, collection, deleteDoc } = await import("firebase/firestore");
            const snap = await getDocs(collection(db, "activityLog"));
            await Promise.all(snap.docs.map(d => deleteDoc(d.ref)));
          }}
        />
      )}
      <CVEditor
        key={activeUserId}
        userId={activeUserId}
        currentUser={activeProfile}
        isImpersonating={!!impersonating}
        onLogout={handleLogout}
        onOpenAdmin={userProfile.role === "admin" ? () => setShowAdmin(true) : null}
        onStopImpersonating={() => setImpersonating(null)}
        fromOutreachOS={sessionStorage.getItem("cursivo_referrer") === "outreachos"}
        appSettings={appSettings}
      />
      </React.Suspense>
      {appNotice && (
        <div role="alert" aria-live="assertive"
          style={{position:"fixed",top:16,right:16,zIndex:3000,maxWidth:380,
            background:"#0f172a",border:"1px solid #1e293b",borderRadius:10,
            padding:"14px 16px 14px 18px",boxShadow:"0 12px 32px rgba(0,0,0,0.5)",
            fontFamily:"-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
            animation:"fadeIn 0.2s ease",
            borderLeft:`4px solid ${appNotice.kind === "error" ? "#ef4444" : "#22c55e"}`}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12}}>
            <div style={{flex:1}}>
              <div style={{color:"#f1f5f9",fontSize:13,fontWeight:700,marginBottom:4}}>
                {appNotice.title}
              </div>
              {appNotice.body && (
                <div style={{color:"#94a3b8",fontSize:12,lineHeight:1.5}}>{appNotice.body}</div>
              )}
            </div>
            <button onClick={() => setAppNotice(null)} aria-label="Dismiss notice"
              style={{background:"none",border:"none",color:"#64748b",cursor:"pointer",
                fontSize:18,lineHeight:1,padding:"0 2px",fontFamily:"inherit"}}>×</button>
          </div>
        </div>
      )}
    </>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <AppInner />
    </ErrorBoundary>
  );
}
